import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { API, useAuth } from "../App";
import { formatApiError } from "../lib/utils";

const VIEWS = {
  paid: { icon: CheckCircle2, color: "text-emerald-400", title: "Payment successful" },
  failed: { icon: XCircle, color: "text-red-400", title: "Payment failed" },
  pending: { icon: Clock, color: "text-yellow-400", title: "Payment processing" },
};

export default function PaymentResult() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const checkoutId = params.get("id");

  useEffect(() => {
    if (!checkoutId) {
      setError("Missing payment reference.");
      return;
    }
    axios
      .get(`${API}/payments/status`, { params: { checkout_id: checkoutId }, withCredentials: true })
      .then((r) => {
        setResult(r.data);
        if (r.data.status === "paid") checkAuth();
      })
      .catch((e) => setError(formatApiError(e, "Could not confirm your payment.")));
  }, [checkoutId]);

  if (!result && !error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="payment-result-loading">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const view = VIEWS[result?.status] || VIEWS.failed;
  const Icon = view.icon;
  const isBanner = result?.kind === "banner";
  const message = error
    || (result.status === "paid"
      ? isBanner
        ? "Thanks! Your banner is paid and will go live once our team approves it."
        : "Your plan is now active. Enjoy your new benefits."
      : result.status === "pending"
        ? "Your bank is still confirming this payment. We'll update your account shortly."
        : result.message || "Your card was not charged. Please try again or use another card.");

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="payment-result-page">
      <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 p-8 text-center">
        <Icon className={`h-12 w-12 mx-auto mb-4 ${view.color}`} />
        <h1 className="font-heading text-3xl text-white mb-2" data-testid="payment-result-status">{error ? "Something went wrong" : view.title}</h1>
        {result && <p className="text-zinc-300 mb-1">{result.description} · R{Number(result.amount).toFixed(2)}</p>}
        <p className="text-zinc-400 mb-6" data-testid="payment-result-message">{message}</p>
        <Button
          onClick={() => navigate(isBanner ? "/advertiser" : result?.status === "paid" ? "/dashboard?tab=billing" : "/pricing")}
          className="bg-orange-600 hover:bg-orange-500"
          data-testid="payment-result-continue-btn"
        >
          {isBanner ? "Go to advertiser dashboard" : result?.status === "paid" ? "Go to my dashboard" : "Back to pricing"}
        </Button>
      </div>
    </div>
  );
}
